import React, { Component, ErrorInfo, ReactNode } from "react";

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

/**
 * Error Boundary component to catch JavaScript errors anywhere in the child component tree
 * and display a fallback UI instead of crashing the entire app
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to console in development
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.error("ErrorBoundary caught an error:", error, errorInfo);
    }

    this.setState({
      error,
      errorInfo,
    });
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="fullscreen-center bg-surface-default text-text-primary p-4">
          <div className="text-center max-w-md">
            <div className="text-6xl mb-4">🔍</div>
            <h1 className="text-2xl font-bold font-title mb-4">Oops! Something went wrong</h1>
            <p className="text-lg mb-6">
              We're sorry, but something unexpected happened. Don't worry, your data is safe!
            </p>

            <div className="space-y-4">
              <button
                onClick={this.handleRetry}
                className="bg-brand-primary hover:bg-brand-primary/80 text-text-primary px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Try Again
              </button>

              <button
                onClick={() => window.location.reload()}
                className="bg-brand-secondary hover:bg-brand-secondary/80 text-text-inverse px-6 py-3 rounded-lg font-medium transition-colors ml-4"
              >
                Reload Page
              </button>
            </div>

            {process.env.NODE_ENV === "development" && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="cursor-pointer text-brand-secondary hover:text-brand-secondary/80">
                  Error Details (Development Only)
                </summary>
                <pre className="mt-2 p-4 bg-surface-alt rounded text-sm overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
